import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    type ArticleValidationProcessStatus,
    CATEGORY_LEGAL_ANNOUCEMENT,
    type DossierArticle,
    REFRESH_CURRENT_DOSSIER_CTX,
    type UpdateArticleParamsType,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierLegalAnnoucements,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { useTheme } from '@mui/material';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierLegalAnnoucementsFooter from '../DossierLegalAnnoucementsFooter/DossierLegalAnnoucementsFooter';
import AddLegalAnnoucementModal from '../AddLegalAnnoucementModal/AddLegalAnnoucementModal';
import { AddLegalAnnoucementFormData } from '../AddLegalAnnoucementForm/AddLegalAnnoucementForm';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import EditLegalAnnoucementForm, {
    EditLegalAnnoucementFormFormData,
} from '../EditLegalAnnoucementForm/EditLegalAnnoucementForm';
import SelectLegalAnnoucementForm from '../SelectLegalAnnoucementForm/SelectLegalAnnoucementForm';
import DossierArticlesProcessesList from '../DossierArticlesProcessesList/DossierArticlesProcessesList';

const DossierLegalAnnoucements: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_LEGAL_ANNOUCEMENT;
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const [processing, setProcessing] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucement>>([]);
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>(true);
    const { prestationArticlesApi } = useFormaliteService();
    const {
        getProcessesByArticle,
        createValidatorProcess,
        canCreateValidatorProcess,
        findProcessByArticle,
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
    } = useDossierProcesses();

    const {
        handleDeleteArticle,
        handlePublishLegalAnnoucement,
        loadLegalAnnoucements,
        actionShowLegalAnouncement,
        actionRemoveLinkedLegalAnouncement,
        actionDeleteArticlePresta,
        actionAttachLegalAnouncement,
        actionPublishLegalAnouncement,
    } = useDossierProcessesActionsAndRender();

    const { addLegalAnnoucement, deleteLegalAnnoucement, getLegalAnnoucementTypes } =
        useDossierLegalAnnoucements();
    const { updateArticle } = useDossierArticle();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { palette } = useTheme();

    const handleAttachLegalAnnoucement = async (idLegalAnnoucement: number, articleId: number) => {
        if (!dossier?.id) {
            return;
        }

        setProcessing(true);

        try {
            const process = findProcessByArticle(dossier, [validatorTypeId], articleId);
            if (!process) {
                throw new Error("Aucun processus de validation en cours sur l'article");
            }

            const request: PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest =
                {
                    id: articleId,
                    apiArticlesPrestationIdAttacherAnnonceLegalePatchRequest: {
                        idAnnonceLegale: idLegalAnnoucement,
                    },
                };

            const response =
                await prestationArticlesApi.apiArticlesPrestationIdAttacherAnnonceLegalePatch(
                    request,
                );

            if (response.status === 200) {
                modalActions.close();
                enqueueSnackbar('Votre annonce a bien été rattachée');

                // Le processus de validation a été mis à jour, récupération du statut lié au valideur
                refreshDossier();

                setLegalAnnoucementsLoaded(false);
            } else {
                enqueueSnackbar(
                    'Une erreur est survenue durant le rattachement de votre annonce légale',
                    { variant: 'error' },
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                'Une erreur est survenue durant le rattachement de votre annonce légale',
                { variant: 'error' },
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseModal = () => {
        modalActions.close();
    };

    const handleDeleteLegalAnnoucement = async (article: DossierArticle) => {
        if (!dossier) {
            return;
        }
        const { status } = await deleteLegalAnnoucement(
            article,
            dossier,
            validatorTypeId,
            'Le rattachement a bien été supprimé',
            'Une erreur est survenue durant la suppression du rattachement',
            'to-link',
        );

        if (status === 200) {
            // Peut être une possibilité plus fine de mettre a jour les données du valideur et de son statut sans avoir a refresh
            refreshDossier();
            setLegalAnnoucementsLoaded(false);
        }

        handleCloseModal();
    };

    const handleAddLegalAnnoucement = async (data: AddLegalAnnoucementFormData) => {
        if (!dossier) {
            return;
        }
        setProcessing(true);

        try {
            const articleId = await addLegalAnnoucement(
                data.title,
                data.prestationId as number,
                data.typeAnnonce as number,
            );
            await createValidatorProcess(dossier.id as number, [articleId]);
            enqueueSnackbar('Votre annonce a bien été ajoutée');
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de votre annonce",
            );
        } finally {
            setProcessing(false);
            setShowAddModal(false);
        }
    };

    const onSubmitEdiLegalAnnoucement = async (data: EditLegalAnnoucementFormFormData) => {
        if (!dossier) {
            return;
        }
        setProcessing(true);
        try {
            await updateArticle({
                prestaArticleId: data.prestaArticleId,
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
                typeArticleId: data.typeAnnonce,
            } as UpdateArticleParamsType);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'édition de la démarche",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleEditLegalAnnoucement = useCallback(
        async (prestaArticle: DossierArticle) => {
            if (!dossier) {
                return;
            }

            const results = await getLegalAnnoucementTypes();
            const legalAnnoucementTypes = results || [];
            const currentType = results.find((legal) => legal.id === prestaArticle.idStype);

            modalActions.call(
                <EditLegalAnnoucementForm
                    onCancel={() => {
                        setProcessing(false);
                        modalActions.close();
                    }}
                    prestaArticle={prestaArticle}
                    onSubmit={onSubmitEdiLegalAnnoucement}
                    dossier={dossier}
                    legalAnnoucementTypes={legalAnnoucementTypes}
                    currentType={currentType}
                />,
            );
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier, modalActions, processing],
    );

    const getItemActions = useCallback(
        (item: DossierArticle) => {
            if (!dossier) {
                return [];
            }
            let actions: Array<DossierArticleProcessMenuItemProps> = [];
            let status: ArticleValidationProcessStatus = 'to-link';
            const processes = getProcessesByArticle(dossier, validatorTypeId, item.id);

            if (processes.length > 0) {
                status = processes[0].statusCode as ArticleValidationProcessStatus;
            }

            const showAnnoucementAction = actionShowLegalAnouncement(
                item.id,
                dossier,
                validatorTypeId,
                legalAnnoucements,
            );

            if (
                ['to-link'].includes(status) &&
                canCreateValidatorProcess(dossier, validatorTypeId)
            ) {
                actions = [
                    ...actions,
                    actionAttachLegalAnouncement(() => {
                        modalActions.call(
                            <SelectLegalAnnoucementForm
                                processing={processing}
                                onItemSelected={handleAttachLegalAnnoucement}
                                article={item}
                                sTypeId={item.idStype}
                                title="Rechercher une annonce légale"
                                placeholder="Rechercher une annonce légale"
                            />,
                        );
                    }),
                    {
                        label: 'Modifier la démarche',
                        icon: 'edit' as IconName,
                        onClick: () => handleEditLegalAnnoucement(item),
                    },
                    actionDeleteArticlePresta(
                        () =>
                            handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                        () => modalActions.close(),
                    ),
                ];
            } else if (['ongoing', 'default', 'rescheduled'].includes(status)) {
                actions = [
                    ...actions,
                    showAnnoucementAction,
                    actionPublishLegalAnouncement(
                        () =>
                            handlePublishLegalAnnoucement(
                                item,
                                dossier,
                                validatorTypeId,
                                () => {
                                    setLegalAnnoucementsLoaded(false);
                                    refreshDossier();
                                },
                                () => handleCloseModal(),
                            ),
                        () => handleCloseModal,
                    ),
                    actionRemoveLinkedLegalAnouncement(
                        handleDeleteLegalAnnoucement,
                        handleCloseModal,
                    ),
                ];
            } else if (['validate'].includes(status)) {
                actions = [...actions, showAnnoucementAction];
            } else if (['failed'].includes(status)) {
                actionDeleteArticlePresta(
                    () => handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                    () => modalActions.close(),
                );
            }

            return actions;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canCreateValidatorProcess,
            dossier,
            getProcessesByArticle,
            legalAnnoucements,
            validatorTypeId,
        ],
    );

    const status = useMemo(
        () =>
            dossier
                ? getArticleProcessesStatus(
                      dossier,
                      validatorTypeId,
                      articles.map((article) => article.id),
                  )
                : 'ongoing',
        [dossier, validatorTypeId, articles, getArticleProcessesStatus],
    );

    useEffect(() => {
        if (!dossier?.id) {
            return;
        }

        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);
            loadLegalAnnoucements(dossier, CATEGORY_LEGAL_ANNOUCEMENT, [validatorTypeId])
                .then((items) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    setLegalAnnoucements([]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    useEffect(() => {
        if (!dossier?.id) {
            return;
        }
        setLegalAnnoucementsLoaded(false);
    }, [dossier?.id]);

    if (!dossier) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }
    return (
        <>
            <Accordion
                title="Annonces légales"
                expandLabel={getArticleProcessesStatusLabel(status)}
                expanded
                expandColor={getArticleProcessesStatusColor(status)}
                sx={{ backgroundColor: palette.background.paper }}
                contentSxProps={{ gap: '16px', display: 'grid' }}
            >
                <DossierArticlesProcessesList
                    articles={articles}
                    dossier={dossier}
                    validatorTypeId={validatorTypeId}
                    legalAnnoucements={legalAnnoucements}
                    getItemActions={getItemActions}
                />
                <DossierLegalAnnoucementsFooter
                    canAddLegalAnnoucement={canCreateValidatorProcess(dossier, validatorTypeId)}
                    handleAddLegalAnnoucement={() => setShowAddModal(true)}
                />
            </Accordion>
            <AddLegalAnnoucementModal
                showModal={showAddModal}
                processing={processing}
                onClose={() => setShowAddModal(false)}
                onSubmit={handleAddLegalAnnoucement}
            />
        </>
    );
};

export default DossierLegalAnnoucements;
